import { useState } from 'react';

import { api } from '../../api';
import { usePolled } from '../../data';
import { ConnectionState, Loading, PageHead, Panel } from '../../ui';
import { Connection } from '../../types';

// My hotels (canon 09 supplier "My hotels" · Register; §4.2 "Hotels"). The
// supplier's own side of the connection graph — the hotels that can book them,
// and the ones still asking. Over the SAME GET /v1/connections the Today card
// reads; a pending row accepts inline through the SAME
// POST /v1/connections/{hotelAccountId}/accept, so there is one accept path, not
// two. No new endpoint.

export function Hotels() {
  const { data, error, reload } = usePolled<{ items: Connection[] }>('/connections');
  const items = data ? data.items : null;
  const rows = (items ?? [])
    .slice()
    // Pending (needs the supplier) first, then active, name-stable within each.
    .sort((a, b) =>
      Number(a.status === 'active') - Number(b.status === 'active') ||
      (a.name ?? '').localeCompare(b.name ?? ''),
    );
  const active = rows.filter((c) => c.status === 'active').length;

  return (
    <div>
      <PageHead title="My hotels" sub="The hotels that can book you — and the ones asking to." />

      {error && items === null && <p className="error-line">{error}</p>}
      {items === null ? (
        <Loading />
      ) : rows.length === 0 ? (
        <div className="empty">
          <strong>No hotels yet</strong>
          <p>When a hotel invites you or asks to connect, it lands here. Accepting lets them book you directly.</p>
        </div>
      ) : (
        <Panel style={{ overflow: 'hidden' }} flush meta={`${active} connected`}>
          <table>
            <thead>
              <tr>
                <th>Hotel</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <HotelRow key={c.hotelAccountId ?? c.accountId} c={c} onAccepted={reload} />
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}

function HotelRow({ c, onAccepted }: { c: Connection; onAccepted: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const hotelId = c.hotelAccountId ?? c.accountId ?? '';

  const accept = async () => {
    setBusy(true);
    setError('');
    try {
      await api('POST', `/connections/${hotelId}/accept`);
      await onAccepted();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <tr>
      <td>{c.name ?? c.hotelName ?? hotelId}</td>
      <td>
        <ConnectionState
          kind={c.status === 'active' ? 'connected' : 'pending'}
          label={c.status === 'active' ? 'Connected' : 'Wants to connect'}
        />
        {error && <p className="error-line" role="alert" style={{ marginTop: 4 }}>{error}</p>}
      </td>
      <td className="num">
        {c.status !== 'active' && (
          <button className="btn" disabled={busy} onClick={accept}>
            {busy ? 'Connecting…' : 'Accept'}
          </button>
        )}
      </td>
    </tr>
  );
}
